import type { AnnotationsProps } from '@repo/image-annotator/lib';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useAtomValue } from 'jotai';
import { useSelector } from 'react-redux';

import { patientInfoAtom } from '../modules/claims/summary/state';

import { makeAPIRequestReactQueryWithHeaders } from './api';
import { endpoints } from './endpoints';

interface GeneratePdfRequest {
  unifiedAppointmentId: string;
  attachmentID?: string;
  narrative?: string;
  clinicalNote?: string;
  fmxTitle?: string;
  toothLevelImages?: Array<{
    title: string;
    imageId: number;
    annotations?: AnnotationsProps;
    tableData: Array<{
      tooth: string;
      txCodes: string[];
      dmf: string;
      caries: boolean;
      resto: boolean;
    }>;
  }>;
  fmxImages?: Array<{
    imageId: number;
    // TODO: Remove annotations from request, migrate to npm package: BOSON-279
    annotations?: AnnotationsProps;
  }>;
  fmxTableData?: Array<{
    tooth: string;
    txCodes: string[];
    calculus: boolean;
    rblMesial: string | null;
    rblDistal: string | null;
  }>;
}

interface GeneratePdfResponse {
  success: boolean;
  message: string;
}

/**
 * React Query mutation hook for generating and downloading PDFs
 *
 * @param options - Optional mutation options
 * @returns React Query mutation object
 */
export const useGeneratePdf = (
  options?: Parameters<
    typeof useMutation<GeneratePdfResponse, AxiosError, GeneratePdfRequest>
  >[0],
) => {
  const user = useSelector(
    (state: {
      login?: {
        user?: { primaryPracticeId?: string; primaryOrganizationId?: string };
      };
    }) => state?.login?.user,
  );
  const currentPracticeId = user?.primaryPracticeId;
  const currentOrganizationId = user?.primaryOrganizationId;
  const patientInfo = useAtomValue(patientInfoAtom);

  return useMutation({
    mutationFn: async (
      request: GeneratePdfRequest,
    ): Promise<GeneratePdfResponse> => {
      try {
        // Create request body, conditionally including fmxImages only if it exists and has items
        const requestBody = {
          unifiedAppointmentId: request.unifiedAppointmentId,
          attachmentID: request.attachmentID,
          narrative: request.narrative,
          clinicalNote: request.clinicalNote,
          fmxTitle: request.fmxTitle,
          toothLevelImages: request.toothLevelImages,
          ...(request.fmxImages &&
            request.fmxImages.length > 0 && { fmxImages: request.fmxImages }),
          ...(request.fmxTableData &&
            request.fmxTableData.length > 0 && {
              fmxTableData: request.fmxTableData,
            }),
        };

        const response = await makeAPIRequestReactQueryWithHeaders<Blob>(
          endpoints.pdfGenerate,
          undefined,
          {
            Practice: currentPracticeId,
            Org: currentOrganizationId,
          },
          'POST',
          {
            data: requestBody,
            responseType: 'blob',
          },
        );

        // Create a download link
        const url = window.URL.createObjectURL(response);
        const link = document.createElement('a');
        link.href = url;

        // Generate file name based on patient name if available, otherwise fall back to unified appointment ID
        // E.g. claim-summary-John-Doe-1234567890
        const filename = patientInfo.name
          ? `claim-summary-${patientInfo.name
              .replace(/[^a-zA-Z0-9\s-]/g, '')
              .replace(/\s+/g, '-')}-${request.unifiedAppointmentId}`
          : `claim-summary-${request.unifiedAppointmentId}`;
        link.download = `${filename}.pdf`;

        // Trigger the download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up the URL object
        window.URL.revokeObjectURL(url);

        return { success: true, message: 'PDF downloaded successfully' };
      } catch (error) {
        console.error('Error generating PDF:', error);
        throw new Error('Failed to generate PDF');
      }
    },
    ...options,
  });
};
